import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { updateProviderSchema, type UpdateProviderInput } from "@/lib/schemas/ai-schemas";
import type { AIProvider } from "@/services/admin-api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

interface EditProviderDialogProps {
  isOpen: boolean;
  onClose: () => void;
  provider: AIProvider | null;
  onSubmit: (data: UpdateProviderInput) => void;
  isPending: boolean;
}

export function EditProviderDialog({
  isOpen,
  onClose,
  provider,
  onSubmit,
  isPending,
}: EditProviderDialogProps) {
  const form = useForm<UpdateProviderInput>({
    resolver: zodResolver(updateProviderSchema),
  });

  useEffect(() => {
    if (provider) {
      form.reset({ name: provider.name, baseUrl: provider.baseUrl });
    }
  }, [provider, form]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="edit-dialog">
        <DialogHeader>
          <DialogTitle>Edit Provider</DialogTitle>
          <DialogDescription>
            Update <strong>{provider?.name}</strong>.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="ep-name">Name</Label>
            <Input id="ep-name" {...form.register("name")} data-testid="edit-name" />
            {form.formState.errors.name && (
              <p className="text-sm text-red-600">{form.formState.errors.name.message}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="ep-url">Base URL</Label>
            <Input id="ep-url" {...form.register("baseUrl")} data-testid="edit-url" />
            {form.formState.errors.baseUrl && (
              <p className="text-sm text-red-600">{form.formState.errors.baseUrl.message}</p>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
